

import React, { useState, useRef, useLayoutEffect, useContext, useEffect } from "react";
import axios from '../axios';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Tag } from 'primereact/tag';
import { Checkbox } from 'primereact/checkbox';
import { Menubar } from 'primereact/menubar';
import { Button } from 'primereact/button'
import { InputText } from 'primereact/inputtext';

import { useMountEffect, useUpdateEffect, useIntersectionObserver, useLocalStorage } from 'primereact/hooks';
import moment from 'moment';
import UsersContext from "../Context/UsersContext";
import { classNames } from "primereact/utils";
import { Skeleton } from 'primereact/skeleton';
import AppContext from "../Context/AppContext";
import LayoutContext from "../Context/LayoutContext";
import { TabPanel, TabView } from "primereact/tabview";
import ListAddModal from "../Modals/ListAddModal";

const Contacts = () => {
    const [lists, setLists] = useState([]);
    const [activeIndex, setActiveIndex] = useState(null)

    const [contacts, setContacts] = useState([]);
    const [skeleton, setSkeleton] = useState(0);
    const [loading, setLoading] = useState(false);

    const [addModal, setAddModal] = useState(false);

    const [currentData, setCurrentData] = useState(null);

    const dataParams = useRef({ offset: 0, limit: 16, count: 0, searchText: "" })
    const lastElementRef = useRef(null);
    const lastElementVisible = useIntersectionObserver(lastElementRef);
    const scrollPosition = useRef(0);
    const networkController = useRef(null)
    const scrollRef = useRef(null)

    const { setNavbar } = useContext(LayoutContext)
    const { isDesktop } = useContext(AppContext)

    const [searchText, setSearchText] = useLocalStorage("", "security.lists.search")

    useEffect(() => {
        setNavbar(
            <div className='flex gap-2'>
                <div className="p-inputgroup max-w-20rem flex-1">
                    <span className="p-inputgroup-addon">
                        <i className="pi pi-search"></i>
                    </span>
                    <InputText placeholder="Search..." value={searchText} onChange={(e) => setSearchText(e.target.value)} />
                </div>
                <Button className="white-space-nowrap" label={"Add List"} onClick={() => setAddModal(true)} />
            </div>
        )
    }, [searchText])

    useMountEffect(() => {
        fetchLists()
    });

    useUpdateEffect(() => {
        if (!lastElementVisible)
           return

        if (activeIndex == null)
            return

        fetchData(lists[activeIndex].id, { ...dataParams.current }, true)
    }, [lastElementVisible])

    useUpdateEffect(() => {
        if (activeIndex == null)
            return

        dataParams.current.searchText = searchText

        fetchData(lists[activeIndex].id, { ...dataParams.current, offset: 0 }, skeleton !== 0)
    }, [searchText]);

    useUpdateEffect(() => {
        if (activeIndex == null)
            return

        fetchData(lists[activeIndex].id, { ...dataParams.current, offset: 0 }, skeleton !== 0)
    }, [activeIndex]);

    useUpdateEffect(() => {
        if (lists.length > 0) {
            setActiveIndex(0)
        }
    }, [lists])

    const fetchLists = async () => {
        try {
            const response = await axios.get('/contacts/lists');

            setLists(response.data)
        } catch (error) {
            if (error?.code === 'ERR_CANCELED') {
                console.log("Request Canceled!");
            } else {
                console.error('Error:', error);
            }
        }
    }

    const fetchData = async (id, params, skeleton = false) => {
        try {
            networkController.current?.abort()
            skeleton ? setSkeleton(params.limit) : setLoading(true)

            networkController.current = new AbortController()
            const response = await axios.get('/contacts', { signal: networkController.current.signal, params: { list_id: id, ...params } });

            console.log(response)

            const _contacts = [...contacts.slice(0, params.offset), ...response.data.data]
            setContacts(_contacts)

            dataParams.current = { ...dataParams.current, count: response.data.count, offset: params.offset + response.data.data.length }

            setSkeleton(0)
            setLoading(false)
        } catch (error) {
            if (error?.code === 'ERR_CANCELED') {
                console.log("Request Canceled!");
            } else {
                console.error('Error:', error);
            }
        }
    };

    const registrationDateTemplate = (value) => {
        return value ? moment(value.registration_date).format("DD MMMM YYYY hh:mm A") : <Skeleton width="11rem" height="26px" borderRadius="16px"></Skeleton>;
    };

    const lastLoginDateTemplate = (value) => {
        return value ? moment(value.last_login).format("DD MMMM YYYY hh:mm A") : <Skeleton width="11rem" height="26px" borderRadius="16px"></Skeleton>;
    };

    const tagTemplate = (value) => {
        return value ? <Tag className='px-2' value={value.tag.toUpperCase()} style={{ color: '#' + value.foreground, backgroundColor: '#' + value.background }} rounded></Tag> : <Skeleton width="4rem" height="26px" borderRadius="16px"></Skeleton>;
    };

    const indexTemplate = (value, props) => {
        return value ? props.rowIndex + 1 : <Skeleton width="2rem" height="26px" borderRadius="16px"></Skeleton>;
    }

    const nameTemplate = (value) => {
        return value ? value.name : <Skeleton width="8rem" height="26px" borderRadius="16px"></Skeleton>;
    }

    const phoneTemplate = (value) => {
        return value ? value.phone : <Skeleton width="8rem" height="26px" borderRadius="16px"></Skeleton>;
    }

    useLayoutEffect(() => {
        scrollRef.current.scrollTo({
            top: scrollPosition.current,
            left: 0,
            behavior: "instant",
        });
    }, [contacts]);

    const handleScroll = (event) => {
        scrollPosition.current = event.target.scrollTop
    }

    return (
        <div className="flex flex-column flex-auto surface-section overflow-scroll align-items-center" ref={scrollRef} onScroll={handleScroll}>
            <div className="w-full datacontent flex-1 container relative px-0 md:px-4">
                <p className="px-4 md:px-0">Last Update: {new Date(lists[activeIndex]?.last_update).toLocaleDateString()} {new Date(lists[activeIndex]?.last_update).toLocaleTimeString()}</p>
                <TabView scrollable activeIndex={activeIndex} onTabChange={(e) => setActiveIndex(e.index)} pt={{ panelContainer: { style: { background: "none", padding: "0px" } } }}>
                    {lists.map((list, index) => {
                        return (
                            <TabPanel key={list.name} header={list.name} pt={{ headerAction: { style: { background: "none" } } }}>
                                <DataTable stripedRows value={contacts.concat(Array(skeleton).fill(null))} className={classNames("", loading && !skeleton ? "opacity-50" : "")}>
                                    <Column field="name" header="#" body={indexTemplate} bodyStyle={{ width: "50px" }}></Column>
                                    <Column field="name" header="Name" body={nameTemplate} bodyStyle={{maxWidth: "0px"}}></Column>
                                    <Column field="phone" header="Phone" body={phoneTemplate} bodyStyle={{maxWidth: "0px"}}></Column>
                                </DataTable>
                                {(!loading && skeleton === 0) && <div className={classNames("absolute bottom-0 w-1rem h-20rem", lastElementVisible || dataParams.current.count === dataParams.current.offset ? "hidden" : "")} ref={lastElementRef}></div>}
                            </TabPanel>
                        );
                    })}
                </TabView>
            </div>

             {addModal && <ListAddModal hide={() => setAddModal(false)} update={() => {
                fetchLists()
            }}></ListAddModal>}
        </div>
    );
}

/**
 * 
 * <Column field="name" header="" body={indexTemplate} pt={{ bodyCell: { className: 'pl-3 md:pl-5' } }} bodyStyle={{ maxWidth: "0px", minWidth: "min-content" }}></Column>
 */

/**
 * 
 * <DataTable size={"small"} value={contacts.concat(Array(skeleton).fill(null))} className={classNames("users-table surface-ground white-space-nowrap", loading && !skeleton ? "opacity-50" : "")}>
                        <Column field="name" header="" body={indexTemplate} pt={{ bodyCell: { className: 'pl-3 md:pl-5' } }} bodyStyle={{maxWidth: "0px", minWidth: "min-content"}}></Column>
                    </DataTable>
                    {(!loading && skeleton === 0) && <div className={classNames("absolute bottom-0 w-1rem h-20rem", lastElementVisible || dataParams.current.count === dataParams.current.offset ? "hidden" : "")} ref={lastElementRef}></div>}
 */

/**
<Column field="name" header="" body={indexTemplate} pt={{ bodyCell: { className: 'pl-3 md:pl-5' } }}></Column>
                        <Column field="name" header="Name" body={nameTemplate} ></Column>
                        {isDesktop && <Column field="registration_date" header="Registration" body={registrationDateTemplate} ></Column>}
 */

/*
{addModal && <UserAddModal hide={() => setAddModal(false)} update={() => {
                fetchData({ ...dataParams.current, offset: 0, limit: dataParams.current.offset })
            }}></UserAddModal>}
            {editModal && <UserEditModal hide={() => setEditModal(false)} data={currentData} update={() => {
                fetchData({ ...dataParams.current, offset: 0, limit: dataParams.current.offset })
            }}></UserEditModal>}
            {deleteModal && <UserDeleteModal hide={() => setDeleteModal(false)} data={currentData} update={() => {
                fetchData({ ...dataParams.current, offset: 0, limit: dataParams.current.offset })
            }}></UserDeleteModal>}
*/

export default Contacts;